import React, { useState } from 'react';

import { useRouter } from 'next/router';

import { getAccessToken } from 'utils/authentication';
import { addZeroToOneDecimal } from 'utils/numbersShowingUtils';

import UseToast from 'hooks/use-toast';
import { getErrorText } from 'utils/geterrortext';

import UseModal from 'hooks/use-modal';
import { MOBILE_SLIDE_UP } from '@core/constants/ModalTypes';
import ChangePlanModal from 'components/pages/settings/ChangePlanModal';
import CancelDiscountModal from 'components/pages/settings/CancelDiscountModal';
import BillingInformation from 'components/pages/settings/BillingInformation';

import { useMutation, useQuery } from 'react-query';
import {
    SubscriptionDeactive,
    GetSubscription,
    GetCustomerPortal,
    SubscriptionReactivate,
    UpgradeSubscription,
    cancelOffer
} from 'services/payments';

import Spinner from '@core/components/spinner/Spinner';
import LinkButton from '@core/components/linkbutton/LinkButton';
import ActionButton from '@core/components/actionbutton/ActionButton';
import Modal from '@core/components/Modal/Modal';
import CancelSubscriptionModal from 'components/pages/settings/CancelSubscriptionModal';
import Loading from '@core/components/loading/Loading';
import { UserOctagon, Card, Edit, UserMinus } from 'iconsax-react';

import ArrowRightIcon from 'assets/icons/arrow-right.svg';

import classes from './AccountSubscription.module.scss';


const AccountSubscription = () => {
    const { toast, ToastContainer } = UseToast();
    const router = useRouter();
    const [cancelReason, setCancelReason] = useState('');
    const [portalButton, setPortalButton] = useState('');
    const { data, isLoading, error, refetch } = useQuery(['subscription-info', getAccessToken()], () => GetSubscription());
    const { data: customerPortalData, isLoading: customePortalIsLoadin, refetch: customePortalRefetch } = useQuery(['customer-portal', getAccessToken()], () => GetCustomerPortal(), {
        onSuccess: (data) => {
            const newWindow = window.open(data?.data?.url, '_blank');
            if (newWindow) {
                newWindow.opener = null;  // Security measure to avoid cross-window communication
            } else {
                router.replace(data?.data?.url);  // Fallback if the new tab/window couldn't be opened
            }
        },
        onError: (error) => {
            toast(getErrorText(error), 'Alert');
        },
        enabled: false
    });
    const { data: cancelOfferData, isLoading: cancelOfferIsLoadin, isRefetching: cancelOfferIsRefetching, refetch: cancelOfferRefetch } = useQuery(['cancel-Offer', getAccessToken()], () => cancelOffer.GET(), {
        onSuccess: (data) => {
            if (data.data.eligible)
                showDiscountSubscriptionModal();
            else
                showCancelSubscriptionModal();
        },
        onError: (error) => {
            showCancelSubscriptionModal();
        },
        enabled: false
    });
    const {
        showModal: showDiscountSubscriptionModal,
        destroyModal: destroyDiscountSubscriptionModal,
        show: showDiscountSubscription
    } = UseModal();
    const {
        showModal: showCancelSubscriptionModal,
        destroyModal: destroyCancelSubscriptionModal,
        show: showCancelSubscription
    } = UseModal();
    const {
        showModal: showConfirmationModal,
        destroyModal: destroyConfirmationModal,
        show: showConfirmation
    } = UseModal();
    const {
        showModal: showChangePlanModal,
        destroyModal: destroyChangePlanModal,
        show: showChangePlan
    } = UseModal();
    const continueCancelHandler = () => {
        destroyCancelSubscriptionModal();
        showConfirmationModal();
    }

    const { mutate } = useMutation(['deactive-subscription', getAccessToken()], SubscriptionDeactive, {
        onSuccess: (data) => {
            toast(
                data?.data?.message,
                'success'
            );
            refetch();
        },
        onError: (err) => {
            toast(getErrorText(err), 'Alert');
        }
    });
    const { mutate: reactivateSubscriptionMutate } = useMutation(['reactivate-subscription', getAccessToken()], SubscriptionReactivate, {
        onSuccess: (data) => {
            toast(
                data?.data?.message,
                'success'
            );
            refetch();
        },
        onError: (err) => {
            toast(getErrorText(err), 'Alert')

        }
    });
    const { mutate: upgradeSubscriptionMutate, isLoading: upgradeSubscriptionIsLoading } = useMutation(['upgrade-subscription', getAccessToken()], UpgradeSubscription, {
        onSuccess: (data) => {
            toast(
                data?.data?.message,
                'success'
            );
            refetch();
            destroyChangePlanModal();
        },
        onError: (err) => {
            toast(getErrorText(err), 'Alert');
        }
    });

    const cancelSubscription = () => {
        mutate({ 'comment': cancelReason });
        destroyConfirmationModal();
    }

    const openCustomerPortal = (selectedPortalButton) => {
        setPortalButton(selectedPortalButton);
        customePortalRefetch();
    }

    const ignorDiscountSubscriptionModal = () => {
        destroyDiscountSubscriptionModal();
        showCancelSubscriptionModal();
    }

    const closeOfferModalHandler = () => {
        refetch();
        destroyDiscountSubscriptionModal();
    }

    return (
        <>
            <div className={classes['subscription-container']}>
                {data && !isLoading && (
                    <div className='row'>
                        <div className="col-12">
                            <div className={classes['plan-wrapper']}>
                                <span className={classes['section-name']}>Plan:</span>
                                <div className='d-flex justify-content-between align-items-center mb-4'>
                                    <div className={classes['plan-name']}>
                                        {data?.data?.plan_name}
                                    </div>
                                    {data?.data?.show_upgrade && (
                                        <div className={classes['plan-item']}>
                                            <ActionButton
                                                kind='secondary'
                                                onClick={() => showChangePlanModal()}
                                            >
                                                Upgrade Plan
                                            </ActionButton>
                                        </div>
                                    )}
                                </div>
                                <div className={`${classes['plan-current']} d-flex justify-content-between align-items-end`}>
                                    {data?.data?.plan_name === 'Free' ? (
                                        <div className={classes['plan-item']}>
                                            <LinkButton kind='primary-color' to={'/plans'}>Go Premium</LinkButton>
                                        </div>
                                    ) : (
                                        <>
                                            {!data?.data?.is_apple_subscription && (
                                                <div className='d-flex flex-column gap-4'>
                                                    <div className={classes['plan-item']}>
                                                        <span>Next Billing Date:</span>  {data?.data?.next_billing_date ? data?.data?.next_billing_date : '-'}
                                                    </div>
                                                    <div className={classes['plan-item']}>
                                                        <span>Next Billing Amount:</span> {data?.data?.next_billing_amount ? <> {data?.data?.currency.symbol}{addZeroToOneDecimal(data?.data?.next_billing_amount)} </> : '-'}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <ActionButton kind='secondary' onClick={() => openCustomerPortal('view')}>
                                        {customePortalIsLoadin && portalButton === 'view' ? <Loading /> : 'View Details & Invoice History'}
                                    </ActionButton>
                                </div>
                            </div>
                            {data?.data?.show_payment_method && (
                                <div className={classes['payment-wrapper']}>
                                    <span className={classes['section-name']}>Payment Information:</span>

                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center py-2">
                                        <p>View the billing card we currently have for your subscription.</p>
                                        <ActionButton kind='secondary' onClick={() => openCustomerPortal('update')}>
                                            {customePortalIsLoadin && portalButton === 'update' ? <Loading /> : <><Edit size={24} /> Update</>}
                                        </ActionButton>
                                    </div>
                                    <span><Card /> ···· ···· ···· ****</span>
                                </div>
                            )}
                            <BillingInformation />
                            {data?.data?.canceled != true && data?.data?.show_payment_method && (
                                <div className={classes['cancel-wrapper']}>
                                    <ActionButton kind='simple'
                                        loading={cancelOfferIsRefetching || cancelOfferIsLoadin}
                                        // onClick={() => { showCancelSubscriptionModal() }}
                                        onClick={() => { cancelOfferRefetch() }}
                                    >
                                        <UserMinus size={24} />
                                        Cancel Subscription
                                    </ActionButton>
                                    <p className='mt-3'>If you wish to discontinue your subscription, you can cancel at anytime.</p>
                                </div>
                            )}
                            {data?.data?.canceled === true && (
                                <div className={classes['reactive-wrapper']}>
                                    <p>
                                       You have canceled your subscription
                                        {data?.data?.cancellation_effective_date != null && (
                                            <> and your premium subscription will be ended at {data?.data?.cancellation_effective_date || data?.data?.cancel_at } </>
                                        )}
                                        , you can Reactive your subscription.
                                    </p>
                                    <ActionButton onClick={() => { reactivateSubscriptionMutate() }}>
                                        Reactive plan
                                    </ActionButton>
                                </div>
                            )}
                            {data?.data?.description && (
                                data?.data?.description
                            )}
                        </div>
                    </div>


                )}

                {isLoading && (
                    <div className="d-flex align-items-center justify-content-center w-100 py-5">
                        <Spinner />
                    </div>
                )}

            </div>
            <Modal
                header={false}
                isGoPermium={true}
                show={showDiscountSubscription}
                type={MOBILE_SLIDE_UP}
                closeHandler={destroyDiscountSubscriptionModal}>
                <CancelDiscountModal
                    continueCancelHandler={ignorDiscountSubscriptionModal}
                    data={cancelOfferData}
                    toast={toast}
                    closeOfferModalHandler={closeOfferModalHandler}
                />
            </Modal>
            <Modal
                title="Cancel Subscription"
                show={showCancelSubscription}
                type={MOBILE_SLIDE_UP}
                closeHandler={destroyCancelSubscriptionModal}>
                <CancelSubscriptionModal
                    cancelReason={cancelReason}
                    setCancelReason={setCancelReason}
                    closeHandler={destroyCancelSubscriptionModal}
                    continueCancelHandler={continueCancelHandler}
                />
            </Modal>
            <Modal
                title="Cancel Subscription"
                show={showConfirmation}
                type={MOBILE_SLIDE_UP}
                closeHandler={destroyConfirmationModal}>
                <div className={classes['confirm-wrapper']}>
                    <p>
                        On {data?.data?.cancellation_effective_date || data?.data?.next_billing_date}, you will be downgraded to our Free plan and will lose access to all Pharmuni Pro courses and tools.
                    </p>
                    <p>
                        Are you sure you'd like to continue with the cancelation of your subscription?
                    </p>
                    <div className="d-flex flex-column flex-md-row gap-2 mt-5 justify-content-end">
                        <ActionButton kind='secondary' onClick={destroyConfirmationModal}>No, Don’t Cancel</ActionButton>
                        <ActionButton onClick={cancelSubscription}>Yes, Cancel Subscription</ActionButton>
                    </div>
                </div>
            </Modal>
            <Modal
                show={showChangePlan}
                type={MOBILE_SLIDE_UP}
                closeHandler={destroyChangePlanModal}>
                <ChangePlanModal
                    changePlanHandler={upgradeSubscriptionMutate}
                    upgradeSubscriptionIsLoading={upgradeSubscriptionIsLoading}
                />
            </Modal>
            <ToastContainer />
        </>
    );
};

export default AccountSubscription;